// One row in ApplicationTrackerTable
// Integrated with EditableCell and CompanySearchModal for intuitive editing

import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApplicationTrackerContext } from '../../contexts/ApplicationTrackerContext';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';
import ExternalLink from '../common/ExternalLink';
import CompanySearchModal from './CompanySearchModal';
import EditableCell from './EditableCell';
import RoleModifyModal from './RoleModifyModal';

// Decoupling Warning Modal Component
const DecoupleWarningModal = ({ isOpen, onClose, onConfirm, companyName, positionName }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-9999 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        ></div>

        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg
                className="h-6 w-6 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
              <h3 className="text-base font-semibold leading-6 text-gray-900">
                Decouple from Job Board?
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  This role is from our job board. Editing it will create a personal copy that's no
                  longer linked to the original job posting.
                </p>
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">
                    Current: {companyName} - {positionName}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    After editing, this will become your personal role
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-lg bg-yellow-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-yellow-500 sm:ml-3 sm:w-auto"
              onClick={onConfirm}
            >
              Continue Editing
            </button>
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple StatusDropdown component for the table
const StatusDropdown = ({ currentStatus, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const statusOptions = [
    'Application Desired',
    'Application Started',
    'Application Submitted',
    'Online Assessment',
    'Live Interviewing',
    'Offer Received',
    'Rejected',
  ];

  const getStatusColor = (status) => {
    const colors = {
      'Application Desired': 'bg-purple-100 text-purple-800 border border-purple-200',
      'Application Started': 'bg-gray-100 text-gray-800 border border-gray-200',
      'Application Submitted': 'bg-blue-100 text-blue-800 border border-blue-200',
      'Online Assessment': 'bg-indigo-100 text-indigo-800 border border-indigo-200',
      'Live Interviewing': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      'Offer Received': 'bg-green-100 text-green-800 border border-green-200',
      Rejected: 'bg-red-100 text-red-800 border border-red-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border border-gray-200';
  };

  // Calculate dropdown position to escape table boundaries
  const handleToggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 300; // Approximate dropdown height

      // Position dropdown above if not enough space below
      const shouldDropUp = rect.bottom + dropdownHeight > viewportHeight;

      setDropdownPosition({
        top: shouldDropUp ? rect.top - dropdownHeight : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 200), // Minimum width of 200px
      });
    }
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleToggleDropdown}
        className={`w-full px-3 py-2 text-left text-xs font-medium rounded-lg transition-colors hover:opacity-80 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${getStatusColor(currentStatus)}`}
      >
        <div className="flex items-center justify-between">
          <span className="truncate">{currentStatus}</span>
          <svg
            className="ml-2 h-4 w-4 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed z-9998 rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          <div className="py-1 max-h-60 overflow-auto">
            {statusOptions.map((status) => (
              <button
                key={status}
                onClick={() => {
                  onStatusChange(status);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50 block ${status === currentStatus ? 'bg-gray-50' : ''}`}
              >
                <span
                  className={`inline-block px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(status)}`}
                >
                  {status}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Tooltip component for lock icon
const Tooltip = ({ children, content, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div
          className={`fixed z-9997 px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-xs max-w-xs ${
            position === 'top'
              ? 'bottom-full left-1/2 transform -translate-x-1/2 mb-2'
              : position === 'bottom'
                ? 'top-full left-1/2 transform -translate-x-1/2 mt-2'
                : position === 'left'
                  ? 'right-full top-1/2 transform -translate-y-1/2 mr-2'
                  : 'left-full top-1/2 transform -translate-y-1/2 ml-2'
          }`}
        >
          {content}
          <div
            className={`tooltip-arrow absolute ${
              position === 'top'
                ? 'top-full left-1/2 transform -translate-x-1/2'
                : position === 'bottom'
                  ? 'bottom-full left-1/2 transform -translate-x-1/2'
                  : position === 'left'
                    ? 'left-full top-1/2 transform -translate-y-1/2'
                    : 'right-full top-1/2 transform -translate-y-1/2'
            }`}
          ></div>
        </div>
      )}
      <style jsx>{`
        .tooltip-arrow {
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-top: 4px solid #111827;
        }
        .tooltip-arrow.bottom-full {
          border-top-color: #111827;
          border-bottom: none;
        }
        .tooltip-arrow.top-full {
          border-bottom: 4px solid #111827;
          border-top: none;
        }
      `}</style>
    </div>
  );
};

function TableRow({ roleID, applicationID, company, position, status, date, isHistory, item }) {
  const [companySearchModalOpen, setCompanySearchModalOpen] = useState(false);
  const [companyName, setCompanyName] = useState(company);
  const [positionName, setPositionName] = useState(position);
  const [currentStatus, setCurrentStatus] = useState(status); // Track current status locally
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [changeLoading, setChangeLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const rowRef = useRef(null);

  // Business logic: determine if this role is from the job board (public)
  const isFromJobBoard = item?.role?.is_public === true || item?.is_public === true;

  // Warning modal state
  const [showDecoupleWarning, setShowDecoupleWarning] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);

  // Role modify modal state
  const [showRoleModifyModal, setShowRoleModifyModal] = useState(false);

  const {
    updateApplication,
    deleteApplication,
    updateApplicationStatus,
    setSelectedApp,
    setAppDetailsOpen,
    setRefreshKey,
  } = useContext(ApplicationTrackerContext);

  const { questPlan, fetchQuest, showToastMessage } = useContext(GlobalContext);

  // Use the last_updated field from item if available, otherwise fall back to date
  const lastUpdatedDate = item?.application?.last_updated || item?.last_updated || date;
  const formattedDate = lastUpdatedDate
    ? new Date(lastUpdatedDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'No date';

  // Update local status when item changes (from backend)
  useEffect(() => {
    const newStatus = item?.application?.status || item?.status;
    if (newStatus) {
      setCurrentStatus(newStatus);
    }
  }, [item?.application?.status, item?.status]);

  // Highlight new applications
  useEffect(() => {
    if (item?.isNew) {
      setIsHighlighted(true);

      if (rowRef.current) {
        const rect = rowRef.current.getBoundingClientRect();
        const isVisible =
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth);

        if (!isVisible) {
          rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      setTimeout(() => {
        setIsHighlighted(false);
      }, 2000);
    }
  }, [item?.isNew]);

  // Handle edit with warning for job board roles
  const handleEditWithWarning = (editType, editFunction, ...args) => {
    if (isFromJobBoard) {
      setPendingEdit({ type: editType, function: editFunction, args });
      setShowDecoupleWarning(true);
    } else {
      editFunction(...args);
    }
  };

  const confirmDecoupleAndEdit = () => {
    if (pendingEdit) {
      pendingEdit.function(...pendingEdit.args);
      setPendingEdit(null);
    }
    setShowDecoupleWarning(false);
  };

  // API Functions for Role Changes
  const changeRoleCompany = async (selectedCompany) => {
    setChangeLoading(true);
    setError(null);

    try {
      if (isFromJobBoard) {
        // For public roles, create a new application (with new private role), then delete the old one
        let createRequest = {
          application: {
            user_id: parseInt(localStorage.getItem('userID')),
          },
          create_new_role: true,
          new_role: {
            role: {
              name: positionName, // Keep existing position name
              description: item?.role?.description || '',
              skills: item?.role?.skills || [],
              location: item?.role?.location || '',
              link: item?.role?.link || '',
              categories: item?.role?.categories || [],
              post_date: item?.role?.post_date || new Date().toISOString().split('T')[0],
              is_active: true,
              programming_languages: item?.role?.programming_languages || [],
              frameworks_and_tools: item?.role?.frameworks_and_tools || [],
              job_focus: item?.role?.job_focus || 'Other',
              visa_status: item?.role?.visa_status || 'Not Specified',
              min_education_level: item?.role?.min_education_level || 'Bachelor',
              min_graduation_year: item?.role?.min_graduation_year || null,
              max_graduation_year: item?.role?.max_graduation_year || null,
              requires_security_clearance: item?.role?.requires_security_clearance || false,
              work_location: item?.role?.work_location || 'On-site',
              internship_season: item?.role?.internship_season || null,
              duration_weeks: item?.role?.duration_weeks || null,
              housing_status: item?.role?.housing_status || 'Not Specified',
              required_gpa: item?.role?.required_gpa || null,
              application_deadline: item?.role?.application_deadline || null,
              company_size: item?.role?.company_size || null,
            },
          },
        };

        if (selectedCompany.isExisting) {
          createRequest.new_role.create_new_company = false;
          createRequest.new_role.existing_company_id = selectedCompany.company_id;
          createRequest.new_role.role.company_id = selectedCompany.company_id;
        } else {
          createRequest.new_role.create_new_company = true;
          createRequest.new_role.new_company = {
            company: {
              company_name: selectedCompany.company_name,
              logo_url: selectedCompany.logo_url,
              linkedin_id: selectedCompany.linkedin_id,
            },
          };
          // For new companies, we can't set company_id yet since it doesn't exist
          // The backend will handle setting this after creating the company
          createRequest.new_role.role.company_id = null;
        }

        // Step 1: Create new application with new role
        const createResponse = await axios.post('/api/v1/application/create', createRequest);
        const createData = createResponse.data;
        const newApplicationId = createData.application_id;

        // Step 2: Create application status for the new application (copy from current status)
        const statusRequest = {
          application_status: {
            application_id: newApplicationId,
            status: currentStatus,
            status_date: new Date().toISOString(),
          },
        };

        try {
          await axios.post('/api/v1/application/status/create', statusRequest);
        } catch (statusError) {
          console.warn(
            'Failed to create status for new application:',
            statusError.response?.data?.detail || statusError.message
          );
          // Don't throw here, status creation failure shouldn't stop the process
        }

        // Step 3: Delete the old application
        try {
          await axios.delete(`/api/v1/application/${applicationID}`);
        } catch (deleteError) {
          console.warn(
            'Failed to delete old application:',
            deleteError.response?.data?.detail || deleteError.message
          );
          // Don't throw here, we already created the new one successfully
        }

        setCompanyName(selectedCompany.company_name);
        updateApplication(
          applicationID,
          company,
          position,
          currentStatus,
          selectedCompany.company_name,
          position,
          currentStatus,
          newApplicationId
        );
        showToastMessage(
          `Company changed to ${selectedCompany.company_name} successfully! Role is now decoupled from job board.`
        );
      } else {
        // For private roles, modify the existing role
        let changeRequest;

        if (selectedCompany.isExisting) {
          changeRequest = {
            create_new_company: false,
            existing_company_id: selectedCompany.company_id,
          };
        } else {
          changeRequest = {
            create_new_company: true,
            new_company: {
              company: {
                company_name: selectedCompany.company_name,
                logo_url: selectedCompany.logo_url,
                linkedin_id: selectedCompany.linkedin_id,
              },
            },
          };
        }

        // Get the role ID from the item structure - try multiple sources for backward compatibility
        const actualRoleId = item?.role?.role_id || item?.application?.role_id || roleID;

        if (!actualRoleId) {
          throw new Error('Role ID not found - cannot modify role');
        }

        const response = await axios.post(
          `/api/v1/role/${actualRoleId}/change-company`,
          changeRequest
        );
        const data = response.data;
        const newRoleId = data.role.role_id;

        await axios.put(`/api/v1/application/${applicationID}`, {
          create_new_role: false,
          existing_role_id: newRoleId,
        });

        setCompanyName(selectedCompany.company_name);
        updateApplication(
          applicationID,
          company,
          position,
          currentStatus,
          selectedCompany.company_name,
          position,
          currentStatus,
          newRoleId
        );
        showToastMessage(`Company changed to ${selectedCompany.company_name} successfully!`);
      }
    } catch (err) {
      console.error('Error changing company:', err);
      setError(err.message);
      showToastMessage(`Failed to change company: ${err.message}`, 'error');
    } finally {
      setChangeLoading(false);
    }
  };

  const changeRoleName = async (newName) => {
    setChangeLoading(true);
    setError(null);

    try {
      if (isFromJobBoard) {
        // For public roles, create a new application (with new private role), then delete the old one
        let createRequest = {
          application: {
            user_id: parseInt(localStorage.getItem('userID')),
          },
          create_new_role: true,
          new_role: {
            role: {
              name: newName, // Use the new name
              description: item?.role?.description || '',
              skills: item?.role?.skills || [],
              location: item?.role?.location || '',
              link: item?.role?.link || '',
              categories: item?.role?.categories || [],
              post_date: item?.role?.post_date || new Date().toISOString().split('T')[0],
              is_active: true,
              programming_languages: item?.role?.programming_languages || [],
              frameworks_and_tools: item?.role?.frameworks_and_tools || [],
              job_focus: item?.role?.job_focus || 'Other',
              visa_status: item?.role?.visa_status || 'Not Specified',
              min_education_level: item?.role?.min_education_level || 'Bachelor',
              min_graduation_year: item?.role?.min_graduation_year || null,
              max_graduation_year: item?.role?.max_graduation_year || null,
              requires_security_clearance: item?.role?.requires_security_clearance || false,
              work_location: item?.role?.work_location || 'On-site',
              internship_season: item?.role?.internship_season || null,
              duration_weeks: item?.role?.duration_weeks || null,
              housing_status: item?.role?.housing_status || 'Not Specified',
              required_gpa: item?.role?.required_gpa || null,
              application_deadline: item?.role?.application_deadline || null,
              company_size: item?.role?.company_size || null,
            },
          },
        };

        // Get the current company ID for the existing role
        try {
          const currentCompanyResponse = await axios.get(
            `/api/v1/company/by-name/${encodeURIComponent(companyName)}`
          );
          const companyData = currentCompanyResponse.data;
          createRequest.new_role.create_new_company = false;
          createRequest.new_role.existing_company_id = companyData.company.company_id;
          createRequest.new_role.role.company_id = companyData.company.company_id;
        } catch (error) {
          // If company not found, create new one
          createRequest.new_role.create_new_company = true;
          createRequest.new_role.new_company = {
            company: {
              company_name: companyName,
              logo_url: null,
              linkedin_id: null,
            },
          };
          // For new companies, we can't set company_id yet since it doesn't exist
          // The backend will handle setting this after creating the company
          createRequest.new_role.role.company_id = null;
        }

        // Step 1: Create new application with new role
        const createResponse = await axios.post('/api/v1/application/create', createRequest);
        const createData = createResponse.data;
        const newApplicationId = createData.application_id;

        // Step 2: Create application status for the new application (copy from current status)
        const statusRequest = {
          application_status: {
            application_id: newApplicationId,
            status: currentStatus,
            status_date: new Date().toISOString(),
          },
        };

        try {
          await axios.post('/api/v1/application/status/create', statusRequest);
        } catch (statusError) {
          console.warn(
            'Failed to create status for new application:',
            statusError.response?.data?.detail || statusError.message
          );
          // Don't throw here, status creation failure shouldn't stop the process
        }

        // Step 3: Delete the old application
        try {
          await axios.delete(`/api/v1/application/${applicationID}`);
        } catch (deleteError) {
          console.warn(
            'Failed to delete old application:',
            deleteError.response?.data?.detail || deleteError.message
          );
          // Don't throw here, we already created the new one successfully
        }

        setPositionName(newName);
        updateApplication(
          applicationID,
          company,
          position,
          currentStatus,
          company,
          newName,
          currentStatus,
          newApplicationId
        );
        showToastMessage(
          `Role name changed to "${newName}" successfully! Role is now decoupled from job board.`
        );
      } else {
        // For private roles, modify the existing role
        // Get the role ID from the item structure - try multiple sources for backward compatibility
        const actualRoleId = item?.role?.role_id || item?.application?.role_id || roleID;

        if (!actualRoleId) {
          throw new Error('Role ID not found - cannot modify role');
        }

        const response = await axios.post(`/api/v1/role/${actualRoleId}/change-name`, {
          name: newName,
        });
        const data = response.data;
        const newRoleId = data.role.role_id;

        await axios.put(`/api/v1/application/${applicationID}`, {
          create_new_role: false,
          existing_role_id: newRoleId,
        });

        setPositionName(newName);
        updateApplication(
          applicationID,
          company,
          position,
          currentStatus,
          company,
          newName,
          currentStatus,
          newRoleId
        );
        showToastMessage(`Role name changed to "${newName}" successfully!`);
      }
    } catch (err) {
      console.error('Error changing role name:', err);
      setError(err.message);
      showToastMessage(`Failed to change role name: ${err.message}`, 'error');
      throw err;
    } finally {
      setChangeLoading(false);
    }
  };

  const updateStatus = (newStatus) => {
    setCurrentStatus(newStatus); // Update local state immediately
    updateApplicationStatus(applicationID, company, position, newStatus);
    // Note: Quest update is now handled in the ApplicationTrackerContext.updateApplicationStatus
    // No need to call updateQuestForApplication here to avoid race conditions
  };

  const handleDelete = () => {
    setIsDeleting(true);
    setTimeout(() => {
      deleteApplication(applicationID, company, position);
    }, 1000);
  };

  // Quest update logic has been moved to ApplicationTrackerContext.updateApplicationStatus
  // to avoid race conditions when updating application status

  return (
    <>
      <tr
        ref={rowRef}
        className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${isDeleting ? 'animate-delete-row' : ''} ${isHighlighted ? 'animate-highlight-row' : ''}`}
      >
        <style jsx>{`
          @keyframes deleteFlash {
            0% {
              background-color: white;
            }
            25% {
              background-color: rgb(254, 202, 202);
            }
            50% {
              background-color: white;
            }
            75% {
              background-color: rgb(254, 202, 202);
            }
            100% {
              background-color: white;
            }
          }
          @keyframes highlightFlash {
            0% {
              background-color: white;
            }
            25% {
              background-color: rgb(219, 234, 254);
            }
            50% {
              background-color: white;
            }
            75% {
              background-color: rgb(219, 234, 254);
            }
            100% {
              background-color: white;
            }
          }
          .animate-delete-row {
            animation: deleteFlash 1s ease-in-out;
            opacity: 0;
            transition: opacity 0.5s ease-in-out;
          }
          .animate-highlight-row {
            animation: highlightFlash 2s ease-in-out;
          }
        `}</style>

        {/* Company Column */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            {isFromJobBoard && (
              <Tooltip content="This role is from our job board. Editing will create a personal copy.">
                <svg
                  className="w-4 h-4 text-gray-400 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </Tooltip>
            )}
            <div className="flex-1">
              <div
                onClick={() =>
                  handleEditWithWarning('company', () => setCompanySearchModalOpen(true))
                }
                className="cursor-pointer hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors group"
                title="Click to change company"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {companyName || 'Click to add company'}
                  </span>
                  <svg
                    className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </div>
              </div>
              {changeLoading && (
                <div className="px-3 py-1">
                  <div className="flex items-center gap-2 text-xs text-blue-600">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                    Updating...
                  </div>
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Position Column */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <EditableCell
                value={positionName}
                onSave={(newName) => handleEditWithWarning('position', changeRoleName, newName)}
                placeholder="Click to add position"
                className="text-sm font-semibold text-gray-900"
              />
            </div>
          </div>
        </td>

        {/* Location Column */}
        <td className="px-6 py-4">
          <span className="text-sm text-gray-600">
            {item?.role?.location || item?.location || 'Not specified'}
          </span>
        </td>

        {/* Internship Season Column */}
        <td className="px-6 py-4">
          <span className="text-sm text-gray-600">
            {item?.role?.internship_season || item?.internship_season || 'Not specified'}
          </span>
        </td>

        {/* Status Column */}
        <td className="px-6 py-4">
          <div className="w-full max-w-[180px]">
            <StatusDropdown currentStatus={currentStatus} onStatusChange={updateStatus} />
          </div>
        </td>

        {/* Actions Column */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-2">
            <button
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
              onClick={() => setIsExpanded(!isExpanded)}
              title={isExpanded ? 'Collapse role details' : 'Expand role details'}
            >
              <svg
                className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <button
              className={`p-2 rounded-lg transition-all duration-200 ${
                isFromJobBoard
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
              }`}
              onClick={() => !isFromJobBoard && setShowRoleModifyModal(true)}
              disabled={isFromJobBoard}
              title={isFromJobBoard ? 'Cannot modify job board roles' : 'Modify role details'}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            <button
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
              onClick={handleDelete}
              title="Delete application"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        </td>
      </tr>

      {/* Expanded Row Content */}
      {isExpanded && (
        <tr className="bg-gray-50">
          <td colSpan="6" className="px-0 py-0">
            <div className="px-6 py-6 space-y-6">
              {/* Role Details Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Role Details</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Basic Information</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Description
                        </label>
                        <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200 min-h-10">
                          {item?.role?.description || 'No description provided'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Job Posting Link
                        </label>
                        <div className="text-sm bg-white px-3 py-2 rounded-lg border border-gray-200">
                          {item?.role?.link ? (
                            <ExternalLink
                              href={item.role.link}
                              className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                            >
                              {item.role.link}
                            </ExternalLink>
                          ) : (
                            <span className="text-gray-500">No link provided</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Post Date
                        </label>
                        <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                          {item?.role?.post_date
                            ? new Date(item.role.post_date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })
                            : 'Unknown'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Application Deadline
                        </label>
                        <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                          {item?.role?.application_deadline
                            ? new Date(item.role.application_deadline).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })
                            : 'No deadline specified'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Requirements */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Requirements</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Job Focus
                          </label>
                          <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                            {item?.role?.job_focus || 'Not specified'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Work Location
                          </label>
                          <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                            {item?.role?.work_location || 'Not specified'}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Visa Status
                          </label>
                          <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                            {item?.role?.visa_status || 'Not specified'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Housing Status
                          </label>
                          <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                            {item?.role?.housing_status || 'Not specified'}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Duration (weeks)
                          </label>
                          <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                            {item?.role?.duration_weeks || 'Not specified'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Required GPA
                          </label>
                          <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                            {item?.role?.required_gpa || 'Not specified'}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Security Clearance
                        </label>
                        <div className="flex items-center bg-white px-3 py-2 rounded-lg border border-gray-200">
                          <div
                            className={`w-2 h-2 rounded-full mr-2 ${item?.role?.requires_security_clearance ? 'bg-green-500' : 'bg-gray-300'}`}
                          ></div>
                          <span className="text-sm text-gray-900">
                            {item?.role?.requires_security_clearance ? 'Required' : 'Not required'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Skills & Technologies Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Skills & Technologies</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Skills */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Required Skills</h4>
                    <div className="bg-white px-3 py-3 rounded-lg border border-gray-200 min-h-12">
                      {item?.role?.skills && item.role.skills.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {item.role.skills.map((skill, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No specific skills listed</span>
                      )}
                    </div>
                  </div>

                  {/* Programming Languages */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Programming Languages
                    </h4>
                    <div className="bg-white px-3 py-3 rounded-lg border border-gray-200 min-h-12">
                      {item?.role?.programming_languages &&
                      item.role.programming_languages.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {item.role.programming_languages.map((lang, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                            >
                              {lang}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">
                          No programming languages specified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Company Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Company Information</h3>
                <div className="bg-white px-4 py-4 rounded-lg border border-gray-200">
                  <div className="flex items-start gap-4">
                    {item?.company?.logo_url && (
                      <img
                        src={item.company.logo_url}
                        alt={`${item.company.company_name} logo`}
                        className="w-12 h-12 rounded-lg object-contain bg-gray-50"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="flex-1 space-y-2">
                      <h4 className="text-lg font-medium text-gray-900">
                        {item?.company?.company_name || companyName}
                      </h4>
                      {item?.company?.linkedin_id && (
                        <ExternalLink
                          href={`https://linkedin.com/company/${item.company.linkedin_id}`}
                          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                          </svg>
                          View on LinkedIn
                        </ExternalLink>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* Company Search Modal */}
      <CompanySearchModal
        isOpen={companySearchModalOpen}
        onClose={() => setCompanySearchModalOpen(false)}
        onSelectCompany={changeRoleCompany}
        currentCompany={companyName}
      />

      {/* Decoupling Warning Modal */}
      <DecoupleWarningModal
        isOpen={showDecoupleWarning}
        onClose={() => setShowDecoupleWarning(false)}
        onConfirm={confirmDecoupleAndEdit}
        companyName={companyName}
        positionName={positionName}
      />

      {/* Role Modify Modal */}
      <RoleModifyModal
        isOpen={showRoleModifyModal}
        onClose={() => setShowRoleModifyModal(false)}
        onSave={async (formData) => {
          try {
            await axios.put(`/api/v1/role/${roleID}`, formData);
            console.log('Role updated successfully');

            // Show success message
            showToastMessage(`Role details updated successfully!`);

            // Refresh the applications to show updated data
            setRefreshKey((prev) => prev + 1);
          } catch (error) {
            console.error('Error updating role:', error);
            showToastMessage(
              `Failed to update role: ${error.response?.data?.detail || error.message}`,
              'error'
            );
          }
        }}
        item={item}
      />

      {/* Error Display */}
      {error && (
        <div className="fixed top-4 right-4 z-9999 max-w-sm">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError(null)}
                  className="inline-flex text-red-400 hover:text-red-600"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TableRow;
